import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { PageHeader, PageContent } from "@/components/layout/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

export default function Settings() {
  const { user } = useAuth();
  const [s, setS] = useState({ ad_revenue: 0, views_alert_threshold: 1000, notify_chat_id: "", notify_bot_token: "" });
  const [accessBot, setAccessBot] = useState({ bot_token: "", username: "", name: "", bot_id: "", status: "Non configurato", online: false });
  const [loading, setLoading] = useState(false);
  const [botTesting, setBotTesting] = useState(false);
  const [webhookLoading, setWebhookLoading] = useState("");

  useEffect(() => {
    api.get("/settings").then(r => setS(prev => ({ ...prev, ...r.data })));
    loadAccessBot();
  }, []);

  const loadAccessBot = async () => {
    const { data } = await api.get("/settings/telegram-access-bot");
    setAccessBot(prev => ({ ...prev, ...data, bot_token: "" }));
  };

  const save = async () => {
    setLoading(true);
    try { const { data } = await api.patch("/settings", {
      ad_revenue: parseFloat(s.ad_revenue)||0,
      views_alert_threshold: parseInt(s.views_alert_threshold)||0,
      notify_chat_id: s.notify_chat_id, notify_bot_token: s.notify_bot_token,
      fb_page_id: s.fb_page_id, fb_page_token: s.fb_page_token, ig_user_id: s.ig_user_id,
      x_api_key: s.x_api_key, x_api_secret: s.x_api_secret,
      x_access_token: s.x_access_token, x_access_secret: s.x_access_secret,
    }); setS({...s, ...data}); toast.success("Impostazioni salvate"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
    finally { setLoading(false); }
  };

  const saveAccessBot = async () => {
    setLoading(true);
    try {
      const { data } = await api.patch("/settings/telegram-access-bot", {
        bot_token: accessBot.bot_token,
        username: accessBot.username,
      });
      setAccessBot(prev => ({ ...prev, ...data }));
      toast.success("Telegram Access Bot salvato");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore salvataggio Access Bot");
    } finally {
      setLoading(false);
    }
  };

  const testAccessBot = async () => {
    setBotTesting(true);
    try {
      const { data } = await api.post("/settings/telegram-access-bot/test", {
        bot_token: accessBot.bot_token,
        username: accessBot.username,
      });
      setAccessBot(prev => ({ ...prev, ...data }));
      if (data.ok) {
        toast.success("Bot connesso correttamente");
      } else {
        toast.error(data.error || "Token non valido");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Test connessione non riuscito");
    } finally {
      setBotTesting(false);
    }
  };

  const configureWebhook = async () => {
    setWebhookLoading("configure");
    try {
      const { data } = await api.post("/settings/telegram-access-bot/webhook/configure");
      setAccessBot(prev => ({ ...prev, ...data }));
      if (data.ok) toast.success("Webhook configurato");
      else toast.error(data.error || "Configurazione webhook non riuscita");
      await loadAccessBot();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Configurazione webhook non riuscita");
    } finally {
      setWebhookLoading("");
    }
  };

  const testWebhook = async () => {
    setWebhookLoading("test");
    try {
      const { data } = await api.post("/settings/telegram-access-bot/webhook/test");
      setAccessBot(prev => ({ ...prev, ...data }));
      if (data.ok) toast.success("Webhook verificato");
      else toast.error(data.error || "Webhook non configurato");
      await loadAccessBot();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Test webhook non riuscito");
    } finally {
      setWebhookLoading("");
    }
  };

  const removeWebhook = async () => {
    setWebhookLoading("remove");
    try {
      const { data } = await api.post("/settings/telegram-access-bot/webhook/remove");
      setAccessBot(prev => ({ ...prev, ...data }));
      if (data.ok) toast.success("Webhook rimosso");
      else toast.error(data.error || "Rimozione webhook non riuscita");
      await loadAccessBot();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Rimozione webhook non riuscita");
    } finally {
      setWebhookLoading("");
    }
  };

  const stats = accessBot.stats || {};
  const lastUser = stats.last_user || null;
  const recentLogs = stats.recent_logs || [];
  const hasAccessBotToken = !!(accessBot.bot_token || accessBot.bot_token_configured);

  const disabled = user?.role !== "admin";

  return (
    <div data-testid="settings-page">
      <PageHeader title="Impostazioni" subtitle="Configura entrate pubblicitarie, soglie e notifiche Telegram." />
      <PageContent>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="bg-slate-900 border border-slate-800 rounded-md p-5">
            <h3 className="font-bold text-white mb-4">Entrate & soglie</h3>
            <div className="space-y-3">
              <div><Label>Entrate pubblicitarie (€)</Label><Input type="number" data-testid="ad-revenue" disabled={disabled} value={s.ad_revenue} onChange={e=>setS({...s, ad_revenue:e.target.value})} className="bg-slate-950 border-slate-800 mt-1" /></div>
              <div><Label>Soglia visualizzazioni per alert</Label><Input type="number" data-testid="alert-threshold" disabled={disabled} value={s.views_alert_threshold} onChange={e=>setS({...s, views_alert_threshold:e.target.value})} className="bg-slate-950 border-slate-800 mt-1" /></div>
            </div>
          </div>
          <div className="bg-slate-900 border border-blue-500/30 rounded-md p-5 ai-glow">
            <h3 className="font-bold text-white mb-1">Notifiche Telegram</h3>
            <p className="text-xs text-slate-400 mb-4">Ricevi alert e log direttamente in una chat/canale Telegram.</p>
            <div className="space-y-3">
              <div><Label>Bot Token per notifiche</Label><Input type="password" data-testid="notify-token" disabled={disabled} value={s.notify_bot_token || ""} onChange={e=>setS({...s, notify_bot_token:e.target.value})} className="bg-slate-950 border-slate-800 mt-1 font-mono" placeholder="123:ABC..." /></div>
              <div><Label>Chat ID destinatario</Label><Input data-testid="notify-chat" disabled={disabled} value={s.notify_chat_id || ""} onChange={e=>setS({...s, notify_chat_id:e.target.value})} className="bg-slate-950 border-slate-800 mt-1" placeholder="@miousername o id numerico" /></div>
            </div>
          </div>
          <div className="bg-slate-900 border border-emerald-500/30 rounded-md p-5 lg:col-span-2">
            <h3 className="font-bold text-white mb-1">Telegram Access Bot</h3>
            <p className="text-xs text-slate-400 mb-4">
              Bot dedicato alla verifica iscrizioni e all'accesso protetto alle live.
            </p>
            <div className="grid gap-4 lg:grid-cols-[420px_1fr]">
              <div className="space-y-3">
                <div>
                  <Label>Bot Token</Label>
                  <Input
                    type="password"
                    disabled={disabled}
                    value={accessBot.bot_token || ""}
                    onChange={e => setAccessBot({ ...accessBot, bot_token: e.target.value })}
                    className="bg-slate-950 border-slate-800 mt-1 font-mono"
                    placeholder={accessBot.bot_token_configured ? `Configurato: ${accessBot.bot_token_preview || "••••"}` : "123456:ABC..."}
                  />
                </div>
                <div>
                  <Label>Username del bot</Label>
                  <Input
                    disabled={disabled}
                    value={accessBot.username || ""}
                    onChange={e => setAccessBot({ ...accessBot, username: e.target.value.replace(/^@/, "") })}
                    className="bg-slate-950 border-slate-800 mt-1"
                    placeholder="CalcioHubAccessBot"
                  />
                </div>

                {!disabled && (
                  <div className="flex flex-wrap gap-2">
                    <Button type="button" onClick={testAccessBot} disabled={botTesting || !hasAccessBotToken} className="bg-emerald-600 hover:bg-emerald-700">
                      {botTesting ? "Test in corso..." : "Test Bot"}
                    </Button>
                    <Button type="button" variant="outline" onClick={saveAccessBot} disabled={loading}>
                      Salva Access Bot
                    </Button>
                    <Button type="button" variant="outline" onClick={configureWebhook} disabled={webhookLoading === "configure" || !hasAccessBotToken}>
                      {webhookLoading === "configure" ? "Configurazione..." : "Configura Webhook"}
                    </Button>
                    <Button type="button" variant="outline" onClick={testWebhook} disabled={webhookLoading === "test" || !hasAccessBotToken}>
                      {webhookLoading === "test" ? "Verifica..." : "Test Webhook"}
                    </Button>
                    <Button type="button" variant="outline" onClick={removeWebhook} disabled={webhookLoading === "remove" || !hasAccessBotToken}>
                      {webhookLoading === "remove" ? "Rimozione..." : "Rimuovi Webhook"}
                    </Button>
                  </div>
                )}
              </div>

              <div className="space-y-3">
                <div className="rounded-md border border-slate-800 bg-slate-950 p-3 text-sm">
                  <div className="grid gap-2 md:grid-cols-2">
                    <div>
                      <div className="text-xs text-slate-500">Nome</div>
                      <div className="text-white">{accessBot.name || "-"}</div>
                    </div>
                    <div>
                      <div className="text-xs text-slate-500">Username</div>
                      <div className="text-white">{accessBot.username ? `@${accessBot.username}` : "-"}</div>
                    </div>
                    <div>
                      <div className="text-xs text-slate-500">ID bot</div>
                      <div className="text-white font-mono">{accessBot.bot_id || "-"}</div>
                    </div>
                    <div>
                      <div className="text-xs text-slate-500">Stato</div>
                      <div className={accessBot.online ? "text-emerald-300" : "text-amber-300"}>
                        {accessBot.status || "Non configurato"}
                      </div>
                    </div>
                    <div>
                      <div className="text-xs text-slate-500">Webhook</div>
                      <div className={accessBot.webhook_status === "Configurato" ? "text-emerald-300" : "text-slate-300"}>{accessBot.webhook_status || "Non configurato"}</div>
                    </div>
                    <div>
                      <div className="text-xs text-slate-500">Ultima chiamata ricevuta</div>
                      <div className="text-white">{accessBot.webhook_last_received_at || "-"}</div>
                    </div>
                    <div>
                      <div className="text-xs text-slate-500">Utenti totali</div>
                      <div className="text-white">{stats.total_users || 0}</div>
                    </div>
                    <div>
                      <div className="text-xs text-slate-500">Nuovi utenti oggi</div>
                      <div className="text-white">{stats.new_today || 0}</div>
                    </div>
                    <div>
                      <div className="text-xs text-slate-500">Utenti attivi oggi</div>
                      <div className="text-white">{stats.active_today || 0}</div>
                    </div>
                    <div>
                      <div className="text-xs text-slate-500">Utenti online</div>
                      <div className="text-white">{stats.users_online || 0}</div>
                    </div>
                    <div>
                      <div className="text-xs text-slate-500">Comandi /start</div>
                      <div className="text-white">{stats.start_commands || 0}</div>
                    </div>
                    <div>
                      <div className="text-xs text-slate-500">Click pulsanti</div>
                      <div className="text-white">{stats.button_clicks || 0}</div>
                    </div>
                    <div>
                      <div className="text-xs text-slate-500">Verifiche oggi</div>
                      <div className="text-white">{stats.verifications_today || 0}</div>
                    </div>
                    <div>
                      <div className="text-xs text-slate-500">Riuscite / Fallite</div>
                      <div className="text-white">{stats.successful_verifications_today || 0} / {stats.failed_verifications_today || 0}</div>
                    </div>
                    <div>
                      <div className="text-xs text-slate-500">Live aperte oggi</div>
                      <div className="text-white">{stats.live_opened_today || 0}</div>
                    </div>
                    <div>
                      <div className="text-xs text-slate-500">Ultimo utente</div>
                      <div className="text-white">{lastUser ? `${lastUser.first_name || ""} ${lastUser.last_name || ""}`.trim() || lastUser.username || lastUser.telegram_user_id : "-"}</div>
                    </div>
                    <div>
                      <div className="text-xs text-slate-500">Ultima attività</div>
                      <div className="text-white">{stats.last_activity?.created_at || "-"}</div>
                    </div>
                  </div>
                  {accessBot.webhook_url && <div className="mt-3 break-all text-xs text-slate-500">Webhook URL: {accessBot.webhook_url}</div>}
                  {accessBot.error && <div className="mt-3 text-xs text-red-300">{accessBot.error}</div>}
                  {accessBot.webhook_error && <div className="mt-3 text-xs text-red-300">{accessBot.webhook_error}</div>}
                  {accessBot.last_test_at && <div className="mt-3 text-xs text-slate-500">Ultimo test: {accessBot.last_test_at}</div>}
                </div>

                <div className="rounded-md border border-slate-800 bg-slate-950 p-3">
                  <div className="mb-2 text-sm font-semibold text-white">Migliori partner</div>
                  <div className="mb-4 space-y-1">
                    {(stats.top_partners || []).length === 0 ? (
                      <div className="text-xs text-slate-500">Nessun dato partner.</div>
                    ) : (stats.top_partners || []).map(item => (
                      <div key={item.id} className="flex items-center justify-between gap-3 text-xs text-slate-300">
                        <span className="truncate">{item.proprietario || item.nome || item.username}</span>
                        <span className="text-emerald-300">{item.conversioni || 0}</span>
                      </div>
                    ))}
                  </div>
                  <div className="mb-2 text-sm font-semibold text-white">Migliori canali</div>
                  <div className="mb-4 space-y-1">
                    {(stats.top_channels || []).length === 0 ? (
                      <div className="text-xs text-slate-500">Nessun dato canale.</div>
                    ) : (stats.top_channels || []).map(item => (
                      <div key={item.id} className="flex items-center justify-between gap-3 text-xs text-slate-300">
                        <span className="truncate">{item.nome || item.username}</span>
                        <span className="text-blue-300">{item.utenti_verificati || 0}</span>
                      </div>
                    ))}
                  </div>
                  <div className="mb-2 text-sm font-semibold text-white">Log messaggi ricevuti</div>
                  <div className="max-h-64 space-y-2 overflow-auto">
                    {recentLogs.length === 0 ? (
                      <div className="text-xs text-slate-500">Nessun messaggio ricevuto.</div>
                    ) : recentLogs.map(log => (
                      <div key={log.id} className="rounded border border-slate-800 p-2 text-xs">
                        <div className="flex flex-wrap items-center justify-between gap-2">
                          <span className="font-medium text-slate-200">{log.event_type || "message"}</span>
                          <span className="text-slate-500">{log.created_at}</span>
                        </div>
                        <div className="mt-1 text-slate-300">
                          {log.first_name || log.username || log.telegram_user_id || "Utente Telegram"}
                        </div>
                        {log.callback_data && <div className="mt-1 break-words text-emerald-300">{log.callback_data}</div>}
                        {log.text && <div className="mt-1 break-words text-slate-400">{log.text}</div>}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="bg-slate-900 border border-slate-800 rounded-md p-5">
          <h3 className="font-bold text-white mb-1">API Social Media</h3>
          <p className="text-xs text-slate-400 mb-4">Configura i token ufficiali per attivare la pubblicazione reale su Facebook, Instagram e X.</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div><Label>Facebook Page ID</Label><Input data-testid="fb-page-id" disabled={disabled} value={s.fb_page_id||""} onChange={e=>setS({...s, fb_page_id:e.target.value})} className="bg-slate-950 border-slate-800 mt-1 font-mono" /></div>
            <div><Label>Facebook Page Token</Label><Input data-testid="fb-token" type="password" disabled={disabled} value={s.fb_page_token||""} onChange={e=>setS({...s, fb_page_token:e.target.value})} className="bg-slate-950 border-slate-800 mt-1 font-mono" /></div>
            <div><Label>Instagram Business User ID</Label><Input data-testid="ig-user-id" disabled={disabled} value={s.ig_user_id||""} onChange={e=>setS({...s, ig_user_id:e.target.value})} className="bg-slate-950 border-slate-800 mt-1 font-mono" /></div>
            <div className="text-[11px] text-slate-500 self-end pb-2">IG Business usa il Page Token di Facebook.</div>
            <div><Label>X API Key</Label><Input type="password" disabled={disabled} value={s.x_api_key||""} onChange={e=>setS({...s, x_api_key:e.target.value})} className="bg-slate-950 border-slate-800 mt-1 font-mono" /></div>
            <div><Label>X API Secret</Label><Input type="password" disabled={disabled} value={s.x_api_secret||""} onChange={e=>setS({...s, x_api_secret:e.target.value})} className="bg-slate-950 border-slate-800 mt-1 font-mono" /></div>
            <div><Label>X Access Token</Label><Input type="password" disabled={disabled} value={s.x_access_token||""} onChange={e=>setS({...s, x_access_token:e.target.value})} className="bg-slate-950 border-slate-800 mt-1 font-mono" /></div>
            <div><Label>X Access Secret</Label><Input type="password" disabled={disabled} value={s.x_access_secret||""} onChange={e=>setS({...s, x_access_secret:e.target.value})} className="bg-slate-950 border-slate-800 mt-1 font-mono" /></div>
          </div>
        </div>
        {!disabled && (
          <Button onClick={save} disabled={loading} data-testid="settings-save" className="bg-blue-600 hover:bg-blue-700">{loading ? "Salvataggio..." : "Salva impostazioni"}</Button>
        )}
        {disabled && <div className="text-xs text-amber-400 bg-amber-600/10 border border-amber-600/30 rounded-md p-3">Solo gli admin possono modificare le impostazioni.</div>}
      </PageContent>
    </div>
  );
}
